/**
 * Configuración centralizada de rutas
 * Organiza todas las rutas de la aplicación por categorías
 * Facilita el mantenimiento y visualización del mapa de rutas
 * 
 * OPTIMIZACIÓN: Implementación de Code Splitting con React.lazy
 * - Reduce el bundle inicial
 * - Mejora FCP (First Contentful Paint) y TTI (Time to Interactive)
 * - Cada página se carga solo cuando el usuario navega a ella
 */

import { lazy } from 'react';
import { BASE_PATHS } from './constants';

// ============================================================================
// PROVIDERS - Se importan normalmente (son ligeros y necesarios para contexto)
// ============================================================================

import { ContentAwardsLandingProvider } from '../contexts/ContentAwardsLandingContext';
import { ContentMicrosoftEcosystemProvider } from '../contexts/ContentMicrosoftEcosystemContext';
import { ContentOrganizationalPoliciesProvider } from '../contexts/ContentOrganizationalPoliciesContext';
import { ContentProductsProvider } from '../contexts/ContentProductsContext';
import { ContentItaasProvider } from '../contexts/ContentItaasContext';
import { ContentSimplePayLandingProvider } from '../contexts/ContentSimplePayLandingContext';
import { ContentSEProvider } from '../contexts/ContentSEContext';
import { ContentStaffAugmentationProvider } from '../contexts/ContentStaffAugmentationContext';
import { ContentDevOpsProvider } from '../contexts/ContentDevOpsContext';
import { ContentHomeProvider } from '../contexts/ContentHomeContext';

// ============================================================================
// PÁGINA HOME - Carga inmediata (ruta crítica)
// El Home se carga de forma estática para garantizar la mejor experiencia
// en la primera visita del usuario (sin mostrar "Cargando...")
// ============================================================================

import { HomeUsa } from '../pages/HomeUsa';

// ============================================================================
// PÁGINAS SECUNDARIAS - Lazy Loading
// Las páginas se cargan bajo demanda cuando el usuario navega a ellas
// ============================================================================

const DedicatedTeams = lazy(() => import('../pages/DedicatedTeams'));
const DevOpsSre = lazy(() => import('../pages/DevOpsSre'));
const SoftwareServices = lazy(() => import('../pages/SoftwareServices'));
const About = lazy(() => import('../pages/About'));
const Careers = lazy(() => import('../pages/Careers'));
const ContactPage = lazy(() => import('../pages/Contact'));
const Helpdesk = lazy(() => import('../pages/Helpdesk'));
const OrganizationalPolicy = lazy(() => import('../pages/OrganizationalPolicy'));
const Page404 = lazy(() => import('../pages/404'));
const StaffAugmentation = lazy(() => import('../pages/StaffAugmentation'));
const MicrosoftEcosystem = lazy(() => import('../pages/MicrosoftEcosystem'));
const Products = lazy(() => import('../pages/Products'));
const Itaas = lazy(() => import('../pages/Itaas'));
const VerticalGenericPage = lazy(() => import('../pages/VerticalGenericPage').then(module => ({ default: module.VerticalGenericPage })));
const SolutionsPage = lazy(() => import('../pages/Solutions').then(module => ({ default: module.SolutionsPage })));
const POCGenericLanding = lazy(() => import('../pages/POCGenericLanding').then(module => ({ default: module.POCGenericLanding })));
const HybridLandingRouter = lazy(() => import('../pages/HybridLandingRouter').then(module => ({ default: module.HybridLandingRouter })));

// Landings
const VivaGoals = lazy(() => import('../landings/vivaGoals'));
const Bdd = lazy(() => import('../landings/bdd'));
const Productividad2023 = lazy(() => import('../landings/productividad3652023'));
const ProductDiscovery = lazy(() => import('../landings/productDiscovery'));
const ITAsAService = lazy(() => import('../landings/iTAsAService'));
const Sre = lazy(() => import('../landings/sre').then(module => ({ default: module.Sre })));
const Mse = lazy(() => import('../landings/mse'));
const Culture = lazy(() => import('../landings/culture').then(module => ({ default: module.Culture })));
const SimplePay = lazy(() => import('../landings/SimplePay'));
const NearshoreUS = lazy(() => import('../landings/houston/NearshoreUS'));
const NearshoreES = lazy(() => import('../landings/houston/NearshoreES'));
const LandingAwards = lazy(() => import('../landings/awards'));

// Landings comerciales
const DevOpsSreLanding = lazy(() => import('../landings/comercial/DevOpsSre'));
const SoftwareEngineeringLanding = lazy(() => import('../landings/comercial/SoftwareEngineering'));
const StaffAugmentationLanding = lazy(() => import('../landings/comercial/StaffAugmentation'));
const MicrosoftEcosystemLanding = lazy(() => import('../landings/comercial/MicrosoftEcosystem'));
const ITaaSLanding = lazy(() => import('../landings/comercial/ITaaS'));
const AISoftwareSolutions = lazy(() => import('../landings/comercial/AISoftwareSolutions'));

// ============================================================================
// EXPORTS DE COMPONENTES LAZY (para uso en AppRouter.js)
// ============================================================================

export {
  VerticalGenericPage,
  SolutionsPage,
};

/**
 * RUTA PRINCIPAL (HOME)
 */
export const homeRoute = {
  path: '/',
  component: HomeUsa,
  provider: ContentHomeProvider,
  exact: true,
};

/**
 * PÁGINAS INSTITUCIONALES
 * Páginas estáticas de información corporativa
 */
export const institutionalRoutes = [
  {
    path: BASE_PATHS.ABOUT,
    component: About,
  },
  {
    path: BASE_PATHS.CAREERS,
    component: Careers,
  },
  {
    path: BASE_PATHS.CONTACT,
    component: ContactPage,
  },
  {
    path: BASE_PATHS.CULTURE,
    component: Culture,
  },
  {
    path: BASE_PATHS.HELPDESK,
    component: Helpdesk,
  },
];

/**
 * SERVICIOS PRINCIPALES
 * Páginas de servicios con providers de contenido dinámico de Strapi
 */
export const serviceRoutes = [
  {
    path: BASE_PATHS.DEDICATED_TEAMS,
    component: DedicatedTeams,
  },
  {
    path: BASE_PATHS.DEVOPS_SRE,
    component: DevOpsSre,
    provider: ContentDevOpsProvider,
  },
  {
    path: BASE_PATHS.SOFTWARE_ENGINEERING,
    component: SoftwareServices,
    provider: ContentSEProvider,
  },
  {
    path: BASE_PATHS.STAFF_AUGMENTATION,
    component: StaffAugmentation,
    provider: ContentStaffAugmentationProvider,
  },
  {
    path: BASE_PATHS.MICROSOFT_ECOSYSTEM,
    component: MicrosoftEcosystem,
    provider: ContentMicrosoftEcosystemProvider,
  },
  {
    path: BASE_PATHS.ITAAS,
    component: Itaas,
    provider: ContentItaasProvider,
  },
];

/**
 * PRODUCTOS
 * Páginas de productos con navegación por secciones
 */
export const productRoutes = [
  {
    path: BASE_PATHS.PRODUCTS,
    component: Products,
    provider: ContentProductsProvider,
  },
  {
    path: BASE_PATHS.PRODUCTS_SECTION,
    component: Products,
    provider: ContentProductsProvider,
  },
  {
    path: BASE_PATHS.ORGANIZATIONAL_POLICY,
    component: OrganizationalPolicy,
    provider: ContentOrganizationalPoliciesProvider,
  },
];

/**
 * AWARDS Y RECONOCIMIENTOS
 */
export const awardsRoutes = [
  {
    path: BASE_PATHS.AWARDS,
    component: LandingAwards,
    provider: ContentAwardsLandingProvider,
  },
];

/**
 * LANDINGS COMERCIALES
 * Landings de servicios para campañas de marketing
 */
export const comercialLandingRoutes = [
  {
    path: BASE_PATHS.COMERCIAL_DEVOPS,
    component: DevOpsSreLanding,
  },
  {
    path: BASE_PATHS.COMERCIAL_SOFTWARE_ENGINEERING,
    component: SoftwareEngineeringLanding,
  },
  {
    path: BASE_PATHS.COMERCIAL_STAFF_AUGMENTATION,
    component: StaffAugmentationLanding,
  },
  {
    path: BASE_PATHS.COMERCIAL_MICROSOFT,
    component: MicrosoftEcosystemLanding,
    provider: ContentMicrosoftEcosystemProvider,
  },
  {
    path: BASE_PATHS.COMERCIAL_ITAAS,
    component: ITaaSLanding,
    provider: ContentItaasProvider,
  },
];

/**
 * SOLUCIONES DINÁMICAS (NUEVO SISTEMA)
 * Sistema de soluciones dinámicas desde Strapi V4
 * NOTA: Las rutas específicas (ej: adopcion-ia, data, microsoft-ecosystem) se manejan directamente en AppRouter.js
 */
export const solutionRoutes = [
  {
    path: BASE_PATHS.SOLUTIONS_INDEX,
    component: SolutionsPage,
  },
  {
    path: BASE_PATHS.SOLUTIONS_DETAIL,
    component: VerticalGenericPage,
  },
];

/**
 * LANDINGS POC GENÉRICAS
 * Sistema de landings POC con contenido dinámico desde Strapi
 */
export const pocLandingRoutes = [
  {
    path: BASE_PATHS.LANDINGS_POC,
    component: HybridLandingRouter,
  },
];

/**
 * LANDINGS ESPECIALES
 * Landings específicas con rutas fijas
 */
export const specialLandingRoutes = [
  {
    path: BASE_PATHS.AI_SOFTWARE_SOLUTIONS,
    component: AISoftwareSolutions,
  },
  {
    path: BASE_PATHS.NEARSHORE_US,
    component: NearshoreUS,
  },
  {
    path: BASE_PATHS.NEARSHORE_ES,
    component: NearshoreES,
  },
  {
    path: BASE_PATHS.SIMPLE_PAY,
    component: SimplePay,
    provider: ContentSimplePayLandingProvider,
  },
];

/**
 * WEBINARS
 * Páginas de webinars y eventos
 */
export const webinarRoutes = [
  {
    path: BASE_PATHS.WEBINAR_ITAAS,
    component: ITAsAService,
  },
  {
    path: BASE_PATHS.WEBINAR_PRODUCT_DISCOVERY,
    component: ProductDiscovery,
  },
  {
    path: BASE_PATHS.WEBINAR_MS_DYNAMICS,
    component: Mse,
  },
  {
    path: BASE_PATHS.WEBINAR_MS_DYNAMICS_ALT,
    component: Mse,
  },
  {
    path: BASE_PATHS.WEBINAR_VIVA_GOALS,
    component: VivaGoals,
  },
  {
    path: BASE_PATHS.WEBINAR_BDD,
    component: Bdd,
  },
  {
    path: BASE_PATHS.WEBINAR_SRE,
    component: Sre,
  },
  {
    path: BASE_PATHS.WEBINAR_PRODUCTIVIDAD,
    component: Productividad2023,
  },
];

/**
 * RUTA 404 (DEBE SER LA ÚLTIMA)
 */
export const notFoundRoute = {
  path: '*',
  component: Page404,
};
